// Allowlist of safe error fields that are guaranteed not to carry user-supplied
// data (request bodies, response bodies, URLs with embedded tokens, headers
// including Authorization bearer tokens, etc.). Any field outside this list is
// dropped before the error is serialised for Application Insights.
//
// Why this exists: previously, a custom JSON.stringify replacer walked
// `Object.getOwnPropertyNames` of an Error and copied every property. For an
// axios error this includes `config` (request URL + headers + body — including
// Authorization), `request`, and `response.data`. All of that was forwarded to
// Application Insights as a single string and persisted. See D2-157 audit
// (HIGH severity).
//
// `status` and `requestId` are on the list because they are provably non-sensitive
// and the most useful for diagnosis: `status` is an integer HTTP status code, and
// `requestId` is a client-generated UUID that correlates a frontend exception to
// its backend log/trace. Neither can carry user data. Everything that can —
// `config`, `request`, `response`, `data`, `headers`, `url` — remains excluded.
export const SAFE_ERROR_FIELDS = ['name', 'message', 'stack', 'code', 'status', 'requestId']

/**
 * Reduce an arbitrary value (typically an Error / axios error) to a plain
 * object containing only the allowlisted safe fields. Non-object inputs are
 * returned unchanged. Any field not in the allowlist — including `config`,
 * `request`, `response`, and any unknown property — is rejected.
 *
 * `baseApiService` rejects with a wrapper object (`{ error, requestId }` for a
 * network failure, `{ ...errorData, status, requestId }` for an HTTP error)
 * rather than a raw Error, so the underlying failure lives one level down under
 * `error`. We recurse a single level — applying the same allowlist — so the real
 * Error's safe fields (name/message/stack/code) survive instead of the whole
 * thing flattening to `{}`. The depth cap keeps a circular reference from
 * recursing forever.
 *
 * @param {unknown} error
 * @param {number} [depth] internal recursion guard; callers should omit it.
 */
export function sanitiseError(error, depth = 0) {
  if (error === null || error === undefined) {
    return error
  }
  if (typeof error !== 'object') {
    return error
  }
  const safe = {}
  for (const field of SAFE_ERROR_FIELDS) {
    // `stack` and `message` are non-enumerable on Error instances; access by key.
    const value = error[field]
    if (value !== undefined) {
      safe[field] = value
    }
  }

  if (depth < 1 && error.error !== null && typeof error.error === 'object') {
    const nested = sanitiseError(error.error, depth + 1)
    if (nested && typeof nested === 'object') {
      for (const field of SAFE_ERROR_FIELDS) {
        if (safe[field] === undefined && nested[field] !== undefined) {
          safe[field] = nested[field]
        }
      }
    }
  }

  return safe
}
